import { makeStyles, Typography } from '@material-ui/core';
import CommitIcon from '@material-ui/icons/Schedule';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

export const LAST_COMMIT_DATE_ANNOTATION = 'git/last-commit-date';

const useStyles = makeStyles(theme => ({
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 0),
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: 12,
        fontWeight: 600,
        fontSize: '0.85rem',
    },
    fresh: {
        backgroundColor: '#e6f4ea',
        color: '#1e7e34',
    },
    stale: {
        backgroundColor: '#fff3cd',
        color: '#856404',
    },
    veryStale: {
        backgroundColor: '#fde8e8',
        color: '#c62828',
    },
    dateText: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
    },
}));

function getRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
}

function getStaleness(date: Date): 'fresh' | 'stale' | 'veryStale' {
    const diffDays = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 30) return 'fresh';
    if (diffDays <= 90) return 'stale';
    return 'veryStale';
}

export const LastCommitCard = () => {
    const classes = useStyles();
    const { entity } = useEntity();

    const rawDate = entity.metadata.annotations?.[LAST_COMMIT_DATE_ANNOTATION];

    if (!rawDate) {
        return (
            <InfoCard title="Last Commit">
                <Typography variant="body2" color="textSecondary">
                    No commit data available. Ensure the repository is hosted on Azure DevOps.
                </Typography>
            </InfoCard>
        );
    }

    const date = new Date(rawDate);
    const staleness = getStaleness(date);
    const badgeClass = {
        fresh: classes.fresh,
        stale: classes.stale,
        veryStale: classes.veryStale,
    }[staleness];

    const formattedDate = date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <InfoCard title="Last Commit">
            <div className={classes.content}>
                <span className={`${classes.badge} ${badgeClass}`}>
                    <CommitIcon style={{ fontSize: '1rem' }} />
                    {getRelativeTime(date)}
                </span>
                <Typography className={classes.dateText}>{formattedDate}</Typography>
            </div>
        </InfoCard>
    );
};
